import React from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql, Link } from 'gatsby';
import slugTransform from 'slug';

import Hamburger from '../styles/hamburgers.min.css';

const MobileNavStyle = styled.div`
	display: none;

	@media all and (max-width: ${(p) => p.theme.burgerBreakpoint}) {
		display: block;
		${Hamburger}

		&.cancelled {
			.hamburger {
				top: 4.5rem;
			}
			.overlay {
				height: calc(100% - 10rem);
				top: 10rem;
			}
			nav {
				top: 10rem;
			}
			@media all and (max-width: 550px) {
				.overlay {
					height: calc(100% - 10.75rem);
					top: 10.75rem;
				}
				nav {
					top: 10.75rem;
				}
				.hamburger {
					top: 5.25rem;
				}
			}
		}

		&.is-active {
			.hamburger {
				position: fixed;
			}
			.overlay {
				position: fixed;
				opacity: 1;
			}
		}

		.overlay {
			width: 100%;
			height: calc(100% - 9rem);
			top: 9rem;
			z-index: 9;
			position: relative;
			opacity: 0;
			transition: opacity 0.3s;
			background-color: rgba(0, 0, 0, 0.5);
		}

		.hamburger {
			z-index: 11;
			position: absolute;
			right: 3rem;
			top: 4rem;
			padding: 1rem;

			.text {
				color: #fff;
				transition: 0.3s color;
				position: relative;
				top: -5px;
				margin-right: 0.5rem;
			}

			.hamburger-inner {
				&::before,
				&::after,
				& {
					background-color: #fff;
				}
			}

			&.is-active:hover,
			&:hover {
				opacity: 1;
			}

			&.is-active {
				position: fixed;
				.hamburger-inner {
					&::before,
					&::after,
					& {
						background-color: #fff;
					}
				}
				.text {
					color: #fff;
				}
			}
		}

		nav {
			position: fixed;
			z-index: 10;
			right: -400px;
			transition: right 0.2s;
			top: 9rem;
			width: 400px;
			background-color: ${(p) => p.theme.color.light};
			border-top: 0.5rem solid ${(p) => p.theme.color.red};
			background-position: -4rem 6rem;
			background-size: 100% auto;
			background-repeat: no-repeat;
			padding: 1rem 3rem 2rem;
			@media all and (max-height: 643px) {
				padding: 1rem 3rem 0;
				&.locations {
					&::after {
						content: '';
						background-image: linear-gradient(
							to top,
							rgba(0, 0, 0, 0.7),
							rgba(0, 0, 0, 0)
						);
						width: 100%;
						height: 2rem;
						position: absolute;
						bottom: 0;
						right: 0;
					}
				}
				ul {
					max-height: calc(100vh - 10.5rem);
					padding-bottom: 2rem;
					overflow-y: scroll;
				}
			}

			ul {
				li {
					padding: 0.6rem 0;
					font-size: 1.3rem;

					a {
						color: ${(p) => p.theme.color.dark};
						text-transform: uppercase;
						&:hover {
							color: ${(p) => p.theme.color.blue};
						}
						&.all-locations {
							color: ${(p) => p.theme.color.red};
						}
					}
					.back-button,
					.submenu-button {
						display: flex;
						align-items: center;
						text-align: left;

						img {
							position: relative;
							left: -5px;
							width: 25px;
							height: 25px;
							display: inline-block;
						}
						span {
							color: ${(p) => p.theme.color.dark};
							font-size: 1.3rem;
							&:hover {
								color: ${(p) => p.theme.color.blue};
							}
						}
					}

					.submenu-button {
						img {
							left: 5px;
						}
						span {
							font-size: 1.3rem;
							&:hover {
								color: ${(p) => p.theme.color.blue};
							}
						}
					}
				}
			}
			&.is-active.lessons {
				right: 0;
			}
			&.is-active.locations {
				right: 0;
			}
			&.is-active.pages {
				right: 0;
			}
		}
	}
	@media all and (max-width: 450px) {
		nav {
			width: 100%;
			right: -100%;
		}
		.hamburger {
			right: 1rem;
			padding: 1rem 0.5rem;
		}
	}
`;

const MobileNav = ({ cancellations, state }) => {
	const { locations, lessons, promo } = useStaticQuery(
		graphql`
			{
				lessons: allLessonsJson(sort: { fields: order, order: ASC }) {
					nodes {
						title
					}
				}
				locations: allLocationsJson(sort: { fields: title, order: ASC }) {
					nodes {
						title
					}
				}
				promo: allPromoJson(filter: { published: { eq: true } }) {
					nodes {
						shortTitle
						slug
					}
				}
			}
		`
	);

	const {
		navIsOpen,
		setNavOpen,
		locationsIsOpen,
		setLocationsOpen,
		lessonsIsOpen,
		setLessonsOpen,
	} = state;

	const navClass =
		navIsOpen || locationsIsOpen || lessonsIsOpen
			? cancellations
				? 'cancelled is-active'
				: 'is-active'
			: cancellations
			? 'cancelled'
			: '';

	const promoEvent = promo.nodes[0];

	return (
		<MobileNavStyle className={navClass}>
			<div
				className="overlay"
				onClick={() => {
					setNavOpen(false);
					setLessonsOpen(false);
					setLocationsOpen(false);
				}}
			/>
			<button
				type="button"
				className={
					navIsOpen || locationsIsOpen || lessonsIsOpen
						? 'hamburger hamburger--spin is-active'
						: 'hamburger hamburger--spin'
				}
				onClick={() => {
					if (navIsOpen || locationsIsOpen || lessonsIsOpen) {
						setNavOpen(false);
						setLessonsOpen(false);
						setLocationsOpen(false);
					} else {
						setNavOpen(true);
					}
				}}
				aria-label="burger menu"
			>
				<span className="text">MENU</span>
				<span className="hamburger-box">
					<span className="hamburger-inner" />
				</span>
			</button>

			<nav className={navIsOpen ? 'pages is-active' : 'pages'}>
				<ul>
					<li>
						<Link to="/about/">ABOUT</Link>
					</li>
					<li>
						<button
							className="submenu-button"
							onClick={() => {
								setLessonsOpen(true);
								setNavOpen(false);
							}}
						>
							<span>LESSONS &amp; PROGRAMS</span>
							<img src="/img/chevron-right.svg" alt="chevron right" />
						</button>
					</li>
					{promoEvent && (
						<li>
							<Link to={`/${promoEvent.slug}/`}>{promoEvent.shortTitle}</Link>
						</li>
					)}
					<li>
						<button
							className="submenu-button"
							onClick={() => {
								setLocationsOpen(true);
								setNavOpen(false);
							}}
						>
							<span>LOCATIONS</span>
							<img src="/img/chevron-right.svg" alt="chevron right" />
						</button>
					</li>
					<li>
						<a
							className="link"
							target="_blank"
							rel="noopener noreferrer"
							href="https://www.cognitoforms.com/ArcadiaAcademyOfMusic1/arcadiaacademyofmusicexamregistration"
						>
							EXAM REGISTRATION
						</a>
					</li>
				</ul>
			</nav>
			<nav className={lessonsIsOpen ? 'lessons is-active' : 'lessons'}>
				<ul>
					<li>
						<button
							className="back-button"
							onClick={() => {
								setNavOpen(true);
								setLessonsOpen(false);
							}}
						>
							<img src="/img/chevron-left.svg" alt="chevron left" />
							<span>BACK</span>
						</button>
					</li>
					{lessons.nodes.map((x, i) => (
						<li key={i}>
							<Link
								to={`/${slugTransform(x.title, { lower: true })}/`}
								onClick={() => setLessonsOpen(false)}
							>
								{x.title}
							</Link>
						</li>
					))}
				</ul>
			</nav>
			<nav className={locationsIsOpen ? 'locations is-active' : 'locations'}>
				<ul>
					<li>
						<button
							className="back-button"
							onClick={() => {
								setNavOpen(true);
								setLocationsOpen(false);
							}}
						>
							<img src="/img/chevron-left.svg" alt="chevron left" />
							<span>BACK</span>
						</button>
					</li>
					{locations.nodes.map((x, i) => (
						<li key={i}>
							<Link
								to={`/${slugTransform(x.title, {
									lower: true,
								})}/`}
								onClick={() => setLocationsOpen(false)}
							>
								{x.title}
							</Link>
						</li>
					))}
					<li>
						<Link
							className="all-locations"
							to="/contact/"
							onClick={() => setLocationsOpen(false)}
						>
							All Locations
						</Link>
					</li>
				</ul>
			</nav>
		</MobileNavStyle>
	);
};

export default MobileNav;
